import React, { useEffect, useState } from "react";
import { Row, Col, Button, Form } from "react-bootstrap";
import styled from "styled-components";
import * as api from "../api/Notifications";
import {
  NotificationSubscription,
  Integration,
  NotificationTopicInfo,
} from "../api/Notifications";

const Section = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h5`
  margin-bottom: 16px;
`;

const SubTitle = styled.p`
  color: #666;
  font-size: 14px;
  margin-bottom: 20px;
`;

const SubscriptionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  margin-bottom: 8px;
  background: #fafafa;
`;

const NameText = styled.span`
  flex: 1;
  min-width: 120px;
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Badge = styled.span<{ integration: Integration }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ integration }) =>
    integration === "tg" ? "#229ED9" : "#2787F5"};
  color: white;
  white-space: nowrap;
`;

const ActiveToggle = styled.div<{ active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  cursor: pointer;
  background: ${({ active }) => (active ? "#52c41a22" : "#f5f5f5")};
  color: ${({ active }) => (active ? "#389e0d" : "#999")};
  border: 1px solid ${({ active }) => (active ? "#b7eb8f" : "#d9d9d9")};
  user-select: none;
  transition: all 0.15s;
  &:hover {
    opacity: 0.85;
  }
`;

const TopicChip = styled.label<{ on: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  cursor: pointer;
  user-select: none;
  background: ${({ on }) => (on ? "#e6f4ff" : "#f5f5f5")};
  color: ${({ on }) => (on ? "#0958d9" : "#999")};
  border: 1px solid ${({ on }) => (on ? "#91caff" : "#d9d9d9")};
  input {
    margin: 0;
  }
`;

const TopicsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const HintBlock = styled.div`
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 20px;
  font-size: 13px;
  line-height: 1.6;
`;

const Notifications: React.FC = () => {
  const [catalog, setCatalog] = useState<NotificationTopicInfo[]>([]);
  const [subscriptions, setSubscriptions] = useState<
    NotificationSubscription[]
  >([]);
  const [chatId, setChatId] = useState("");
  const [name, setName] = useState("");
  const [integration, setIntegration] = useState<Integration>("tg");
  const [topics, setTopics] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [testResult, setTestResult] = useState<Record<number, "ok" | "error">>(
    {}
  );

  useEffect(() => {
    Promise.all([api.getNotificationTopics(), api.getSubscriptions()])
      .then(([topicList, subs]) => {
        setCatalog(topicList);
        setTopics(topicList.filter((t) => t.defaultOn !== false).map((t) => t.id));
        setSubscriptions(subs);
      })
      .catch(console.error);
  }, []);

  const toggleTopicDraft = (topicId: string) => {
    setTopics((prev) =>
      prev.includes(topicId)
        ? prev.filter((t) => t !== topicId)
        : [...prev, topicId]
    );
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedId = chatId.trim();
    const trimmedName = name.trim();
    if (!trimmedId || !trimmedName) return;
    if (!topics.length) {
      alert("Выберите хотя бы один тип уведомлений");
      return;
    }
    setAdding(true);
    try {
      const sub = await api.addSubscription(
        trimmedId,
        integration,
        trimmedName,
        topics
      );
      setSubscriptions((prev) => [sub, ...prev]);
      setChatId("");
      setName("");
      setTopics(catalog.filter((t) => t.defaultOn !== false).map((t) => t.id));
    } catch (err: any) {
      const msg =
        err?.status === 409
          ? "Подписка с таким Chat ID и интеграцией уже существует"
          : "Не удалось добавить подписку";
      alert(msg);
    } finally {
      setAdding(false);
    }
  };

  const handleToggle = async (sub: NotificationSubscription) => {
    const updated = await api.updateSubscription(sub.id, {
      is_active: !sub.is_active,
    });
    setSubscriptions((prev) =>
      prev.map((s) => (s.id === sub.id ? updated : s))
    );
  };

  const handleTopicsChange = async (
    sub: NotificationSubscription,
    topicId: string,
    checked: boolean
  ) => {
    const current = Array.isArray(sub.topics) ? sub.topics : [];
    const next = checked
      ? [...new Set([...current, topicId])]
      : current.filter((t) => t !== topicId);
    if (!next.length) {
      alert("Нужен хотя бы один тип уведомлений");
      return;
    }
    try {
      const updated = await api.updateSubscription(sub.id, { topics: next });
      setSubscriptions((prev) =>
        prev.map((s) => (s.id === sub.id ? updated : s))
      );
    } catch {
      alert("Не удалось сохранить типы");
    }
  };

  const handleDelete = async (id: number) => {
    await api.deleteSubscription(id);
    setSubscriptions((prev) => prev.filter((s) => s.id !== id));
  };

  const handleTest = async (id: number) => {
    setTestingId(id);
    setTestResult((prev) => ({ ...prev, [id]: undefined as any }));
    try {
      await api.testSubscription(id);
      setTestResult((prev) => ({ ...prev, [id]: "ok" }));
    } catch {
      setTestResult((prev) => ({ ...prev, [id]: "error" }));
    } finally {
      setTestingId(null);
    }
  };

  return (
    <>
      <Section>
        <Title>Уведомления</Title>
        <SubTitle>
          Выберите тематики для каждой подписки. Список тематик расширяется на
          сервере — после обновления каталога новые типы появятся здесь сами.
        </SubTitle>

        <HintBlock>
          {catalog.map((t) => (
            <div key={t.id} style={{ marginBottom: 6 }}>
              <b>{t.label}</b> — {t.description}
            </div>
          ))}
          <div style={{ marginTop: 10 }}>
            <b>Chat ID:</b> напишите <code>/start</code> или{" "}
            <code>/chatid</code> боту Telegram/Max.
          </div>
        </HintBlock>
      </Section>

      <Section>
        <Form onSubmit={handleAdd}>
          <Row className="align-items-end g-2">
            <Col xs={12} md={3}>
              <Form.Label style={{ fontSize: 13 }}>Название</Form.Label>
              <Form.Control
                type="text"
                placeholder="Например: Мой Telegram"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Col>
            <Col xs={12} md={3}>
              <Form.Label style={{ fontSize: 13 }}>Chat ID</Form.Label>
              <Form.Control
                type="text"
                placeholder="Например: 123456789"
                value={chatId}
                onChange={(e) => setChatId(e.target.value)}
                required
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label style={{ fontSize: 13 }}>Интеграция</Form.Label>
              <Form.Select
                value={integration}
                onChange={(e) =>
                  setIntegration(e.target.value as Integration)
                }
              >
                <option value="tg">Telegram</option>
                <option value="max">Max</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={2}>
              <Form.Label style={{ fontSize: 13 }}>Типы</Form.Label>
              <TopicsRow>
                {catalog.map((t) => (
                  <TopicChip key={t.id} on={topics.includes(t.id)} title={t.description}>
                    <input
                      type="checkbox"
                      checked={topics.includes(t.id)}
                      onChange={() => toggleTopicDraft(t.id)}
                    />
                    {t.label}
                  </TopicChip>
                ))}
              </TopicsRow>
            </Col>
            <Col xs={6} md={2}>
              <Button type="submit" disabled={adding} style={{ width: "100%" }}>
                {adding ? "..." : "Добавить"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Section>

      <Section>
        {subscriptions.length === 0 ? (
          <p style={{ color: "#999", fontSize: 14 }}>
            Подписок нет. Добавьте первую выше.
          </p>
        ) : (
          subscriptions.map((sub) => {
            const subTopics = Array.isArray(sub.topics) ? sub.topics : [];
            return (
              <SubscriptionRow key={sub.id}>
                <Badge integration={sub.integration}>
                  {sub.integration === "tg" ? "Telegram" : "Max"}
                </Badge>
                <NameText>{sub.name}</NameText>
                <TopicsRow>
                  {catalog.map((t) => (
                    <TopicChip
                      key={t.id}
                      on={subTopics.includes(t.id)}
                      title={t.description}
                    >
                      <input
                        type="checkbox"
                        checked={subTopics.includes(t.id)}
                        onChange={(e) =>
                          handleTopicsChange(sub, t.id, e.target.checked)
                        }
                      />
                      {t.label}
                    </TopicChip>
                  ))}
                </TopicsRow>
                <ActiveToggle
                  active={sub.is_active}
                  onClick={() => handleToggle(sub)}
                >
                  {sub.is_active ? "✓ Включено" : "Выключено"}
                </ActiveToggle>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={testingId === sub.id}
                  onClick={() => handleTest(sub.id)}
                  title="Отправить тестовое сообщение"
                >
                  {testingId === sub.id
                    ? "..."
                    : testResult[sub.id] === "ok"
                    ? "✓ Отправлено"
                    : testResult[sub.id] === "error"
                    ? "✗ Ошибка"
                    : "Тест"}
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleDelete(sub.id)}
                >
                  Удалить
                </Button>
              </SubscriptionRow>
            );
          })
        )}
      </Section>
    </>
  );
};

export default Notifications;
